import React from 'react';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { Alert, Box, Button, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import AdminSurface from '../../../components/admin/AdminSurface';
import AdminConfirmationDialog from '../../../components/admin/AdminConfirmationDialog';
import { adminColors, adminTypography } from '../../../styles/adminDesignTokens';
import {
  adminButtonSx,
  adminButtonToneSx,
  adminCombineSx,
} from '../../../styles/adminComponentStyles';
import FeatureFlagDrawer from './FeatureFlagDrawer';
import FeatureFlagClientConfigurationPanel from './FeatureFlagClientConfigurationPanel';
import FeatureFlagMetadataForm from './FeatureFlagMetadataForm';
import FeatureFlagTargetClientSelector from './FeatureFlagTargetClientSelector';
import FeatureFlagsTable from './FeatureFlagsTable';
import { useFeatureFlagsPageControls } from '../hooks/useFeatureFlagsPageControls';

const FeatureFlagsPageContent: React.FC = () => {
  const {
    activeClientConfiguration,
    activeTargetClient,
    assignmentIdsLoading,
    clientFilter,
    clientConfigurations,
    confirmationDialogContent,
    drawerMode,
    drawerOpen,
    featureFieldErrors,
    featureFormError,
    featureMetadataValues,
    featureRows,
    featureRowsError,
    featureRowsLoading,
    featureSubmitting,
    filteredStoreOptions,
    handleActiveTargetClientChange,
    handleAssignmentTargetChange,
    handleBulkStoreAssignmentChange,
    handleBulkUserAssignmentChange,
    handleClientFilterChange,
    handleCloseDrawer,
    handleConfirmationCancel,
    handleConfirmationConfirm,
    handleFeatureMetadataFieldChange,
    handleGlobalEnabledChange,
    handleOpenCreateDrawer,
    handleOpenUpdateDrawer,
    handleSaveFeature,
    handleSearchChange,
    handleStoreAssignmentChange,
    handleStoreSearchChange,
    handleStoreStatusFilterChange,
    handleTableSort,
    handleTargetClientSelectedChange,
    handleUserAssignmentChange,
    search,
    storeOptionsError,
    storeOptionsLoading,
    storeSearch,
    storeStatusFilter,
    tableSortDirection,
    tableSortKey,
  } = useFeatureFlagsPageControls();

  return (
    <Box>
      <Box
        sx={{
          alignItems: { xs: 'stretch', lg: 'flex-start' },
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          gap: 2,
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ ...adminTypography.pageTitle, color: adminColors.text }}>
            Feature Flags
          </Typography>
          <Typography sx={{ ...adminTypography.pageMeta, color: adminColors.mutedText, mt: 0.5 }}>
            Manage frontend feature access by client app and rollout target.
          </Typography>
        </Box>

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}
          sx={{ flexShrink: 0 }}
        >
          <ToggleButtonGroup
            exclusive
            size="small"
            value={clientFilter}
            onChange={handleClientFilterChange}
            sx={{
              height: 40,
              '& .MuiToggleButton-root': {
                borderColor: '#D6DBE1',
                color: '#2B3445',
                fontSize: '13px',
                fontWeight: 700,
                letterSpacing: 0,
                px: 1.5,
                textTransform: 'none',
              },
              '& .Mui-selected': {
                backgroundColor: '#2C6CA0 !important',
                color: '#ffffff !important',
              },
            }}
          >
            <ToggleButton value="webapp">
              Web App
            </ToggleButton>
            <ToggleButton value="mobile_app">
              Mobile App
            </ToggleButton>
          </ToggleButtonGroup>

          <TextField
            fullWidth
            label="Search Features"
            size="small"
            value={search}
            onChange={handleSearchChange}
            placeholder="Search by feature name..."
            inputProps={{ 'aria-label': 'Search features' }}
            sx={{
              maxWidth: { md: 340 },
              width: { xs: '100%', md: 340 },
              '& .MuiInputBase-root': { height: 40 },
            }}
          />

          <Button
            variant="contained"
            size="small"
            startIcon={<AddRoundedIcon />}
            onClick={handleOpenCreateDrawer}
            sx={adminCombineSx(adminButtonSx, adminButtonToneSx.primary, {
              height: 40,
              minWidth: { xs: '100%', md: 144 },
              minHeight: 40,
            })}
          >
            Create Feature
          </Button>
        </Stack>
      </Box>

      <AdminSurface sx={{ overflow: 'hidden' }}>
        <FeatureFlagsTable
          error={featureRowsError}
          loading={featureRowsLoading}
          rows={featureRows}
          order={tableSortDirection}
          orderBy={tableSortKey}
          onRowClick={handleOpenUpdateDrawer}
          onRequestSort={handleTableSort}
        />
      </AdminSurface>

      <FeatureFlagDrawer
        mode={drawerMode}
        open={drawerOpen}
        onClose={handleCloseDrawer}
        onPrimaryAction={handleSaveFeature}
        primaryButtonDisabled={assignmentIdsLoading}
        submitting={featureSubmitting}
      >
        <Stack spacing={2}>
          {featureFormError ? (
            <Alert severity="error" sx={{ alignItems: 'center' }}>
              {featureFormError}
            </Alert>
          ) : null}
          <FeatureFlagMetadataForm
            fieldErrors={featureFieldErrors}
            mode={drawerMode}
            values={featureMetadataValues}
            onFieldChange={handleFeatureMetadataFieldChange}
          />
          <FeatureFlagTargetClientSelector
            activeClient={activeTargetClient}
            configurations={clientConfigurations}
            onActiveClientChange={handleActiveTargetClientChange}
            onClientSelectedChange={handleTargetClientSelectedChange}
            selectionDisabled={drawerMode === 'update'}
            targetClientError={featureFieldErrors.targetClient}
          />
          <FeatureFlagClientConfigurationPanel
            activeClient={activeTargetClient}
            assignmentIdsLoading={assignmentIdsLoading}
            configuration={activeClientConfiguration}
            storeSearch={storeSearch}
            stores={filteredStoreOptions}
            onAssignmentTargetChange={handleAssignmentTargetChange}
            onBulkStoreAssignmentChange={handleBulkStoreAssignmentChange}
            onBulkUserAssignmentChange={handleBulkUserAssignmentChange}
            onGlobalEnabledChange={handleGlobalEnabledChange}
            onStoreAssignmentChange={handleStoreAssignmentChange}
            onStoreSearchChange={handleStoreSearchChange}
            onStoreStatusFilterChange={handleStoreStatusFilterChange}
            onUserAssignmentChange={handleUserAssignmentChange}
            storeIdsError={featureFieldErrors.storeIds}
            userIdsError={featureFieldErrors.userIds}
            storeOptionsError={storeOptionsError}
            storeOptionsLoading={storeOptionsLoading}
            storeStatusFilter={storeStatusFilter}
          />
        </Stack>
      </FeatureFlagDrawer>

      {confirmationDialogContent ? (
        <AdminConfirmationDialog
          confirmButtonText={confirmationDialogContent.confirmButtonText}
          confirmTone={confirmationDialogContent.confirmTone}
          message={confirmationDialogContent.message}
          open
          title={confirmationDialogContent.title}
          onCancel={handleConfirmationCancel}
          onConfirm={handleConfirmationConfirm}
        />
      ) : null}
    </Box>
  );
};

export default FeatureFlagsPageContent;
